import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { workspaceAPI, extractErrorMessage, extractErrorCode } from '../../../services/api';
import type { Workspace, WorkspaceInvite, WorkspaceMember, WorkspaceRole } from '../../../types';

export interface MembersMessage {
  type: 'success' | 'error';
  text: string;
}

export function useMembersTab() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);
  const [inviteFilter, setInviteFilter] = useState<'open' | 'all'>('open');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<MembersMessage | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const currentMember = useMemo(
    () => (user ? members.find((m) => m.id === user.id) ?? null : null),
    [members, user]
  );
  const isAdmin = currentMember?.role === 'admin';
  const activeAdminCount = useMemo(
    () => members.filter((m) => m.role === 'admin' && m.status !== 'disabled').length,
    [members]
  );

  const refreshMembers = useCallback(async () => {
    const rows = await workspaceAPI.listMembers();
    setMembers(rows);
  }, []);

  const refreshInvites = useCallback(
    async (status: 'open' | 'all' = inviteFilter) => {
      const rows = await workspaceAPI.listInvites(status);
      setInvites(rows);
    },
    [inviteFilter]
  );

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [ws, memberRows, inviteRows] = await Promise.all([
        workspaceAPI.getWorkspace(),
        workspaceAPI.listMembers(),
        workspaceAPI.listInvites(inviteFilter),
      ]);
      setWorkspace(ws);
      setMembers(memberRows);
      setInvites(inviteRows);
    } catch (err: any) {
      setLoadError(extractErrorMessage(err, 'Failed to load workspace.'));
    } finally {
      setLoading(false);
    }
  }, [inviteFilter]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (message?.type === 'success') {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const renameWorkspace = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      setMessage({ type: 'error', text: 'Workspace name is required.' });
      return false;
    }
    setBusyId('workspace');
    try {
      const updated = await workspaceAPI.renameWorkspace(trimmed);
      setWorkspace(updated);
      setMessage({ type: 'success', text: 'Workspace renamed.' });
      return true;
    } catch (err: any) {
      setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to rename workspace.') });
      return false;
    } finally {
      setBusyId(null);
    }
  }, []);

  const changeRole = useCallback(
    async (userId: string, role: WorkspaceRole) => {
      setBusyId(userId);
      try {
        await workspaceAPI.changeMemberRole(userId, role);
        await refreshMembers();
        setMessage({ type: 'success', text: 'Member role updated.' });
        return true;
      } catch (err: any) {
        const code = extractErrorCode(err);
        const text =
          code === 'last_admin'
            ? "Can't demote the last admin — promote another member first."
            : extractErrorMessage(err, 'Failed to update role.');
        setMessage({ type: 'error', text });
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [refreshMembers]
  );

  const removeMember = useCallback(
    async (userId: string) => {
      setBusyId(userId);
      try {
        await workspaceAPI.removeMember(userId);
        await refreshMembers();
        setMessage({ type: 'success', text: 'Member removed.' });
        return true;
      } catch (err: any) {
        const code = extractErrorCode(err);
        const text =
          code === 'last_admin'
            ? "Can't remove the last admin."
            : extractErrorMessage(err, 'Failed to remove member.');
        setMessage({ type: 'error', text });
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [refreshMembers]
  );

  const createInvite = useCallback(
    async (email: string, role: WorkspaceRole): Promise<{ ok: true } | { ok: false; code: string | null; message: string }> => {
      setBusyId('new-invite');
      try {
        await workspaceAPI.createInvite(email.trim(), role);
        await refreshInvites();
        setMessage({ type: 'success', text: 'Invite sent.' });
        return { ok: true };
      } catch (err: any) {
        const code = extractErrorCode(err);
        const msg = extractErrorMessage(err, 'Failed to send invite.');
        return { ok: false, code, message: msg };
      } finally {
        setBusyId(null);
      }
    },
    [refreshInvites]
  );

  const revokeInvite = useCallback(
    async (inviteId: string) => {
      setBusyId(inviteId);
      try {
        await workspaceAPI.revokeInvite(inviteId);
        await refreshInvites();
        setMessage({ type: 'success', text: 'Invite revoked.' });
        return true;
      } catch (err: any) {
        setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to revoke invite.') });
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [refreshInvites]
  );

  const resendInvite = useCallback(
    async (inviteId: string) => {
      setBusyId(inviteId);
      try {
        await workspaceAPI.resendInvite(inviteId);
        await refreshInvites();
        setMessage({ type: 'success', text: 'Invite resent.' });
        return true;
      } catch (err: any) {
        const code = extractErrorCode(err);
        const map: Record<string, string> = {
          invite_already_accepted: 'This invite has already been accepted.',
          invite_revoked: 'This invite was revoked — create a new one.',
        };
        const text = (code && map[code]) || extractErrorMessage(err, 'Failed to resend invite.');
        setMessage({ type: 'error', text });
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [refreshInvites]
  );

  const changeInviteFilter = useCallback(async (next: 'open' | 'all') => {
    setInviteFilter(next);
    try {
      const rows = await workspaceAPI.listInvites(next);
      setInvites(rows);
    } catch (err: any) {
      setMessage({ type: 'error', text: extractErrorMessage(err, 'Failed to load invites.') });
    }
  }, []);

  return {
    workspace,
    members,
    invites,
    inviteFilter,
    loading,
    loadError,
    message,
    setMessage,
    busyId,
    isAdmin,
    currentMember,
    activeAdminCount,
    refreshAll,
    renameWorkspace,
    changeRole,
    removeMember,
    createInvite,
    revokeInvite,
    resendInvite,
    changeInviteFilter,
  };
}
